import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import lottie from 'lottie-web';



// Utility function to convert milliseconds to seconds
function msToSeconds(...values) {
    return values.map(value => value * 0.001);
}

const defaultConfig = {
    duration: 750,
    delay: 300,
    start: 'top 90%',
    end: 'bottom top',
    disableOnMobile: false,
    playOnce: true,
    mobileBreakpoint: 768,
    detectMethod: 'auto', // 'auto', 'screensize', 'touch', 'useragent'
    pinDuration: 800, // Configurable pin duration for text blocks
    animateOnLoadOnly: true // Skip scroll trigger and animate on load only
};

const animationLibrary = {
    fadeIn: {
        from: { opacity: 0 },
        to: { opacity: 1 }
    },
    fadeOut: {
        from: { opacity: 1 },
        to: { opacity: 0 }
    },
    zoomIn: {
        from: { scale: 0.75, opacity: 0 },
        to: { scale: 1, opacity: 1 }
    },
    zoomOut: {
        from: { scale: 1, opacity: 1 },
        to: { scale: 0.5, opacity: 0 }
    },
    slideIn: {
        from: { x: '100%', opacity: 0 },
        to: { x: '0%', opacity: 1 }
    },
    slideOut: {
        from: { x: '0%', opacity: 1 },
        to: { x: '-100%', opacity: 0 }
    },
    slideUp: {
        from: { y: '8rem', opacity: 0 },
        to: { y: '0rem', opacity: 1 }
    },
    slideUpNoOpacity: {
        from: { y: '8rem' },
        to: { y: '0rem' }
    }
};

// Include GSAP and ScrollTrigger
gsap.registerPlugin(ScrollTrigger);

// Function to check if we're on mobile (configurable detection)
function isMobile() {
    const breakpoint = defaultConfig.mobileBreakpoint;
    const method = defaultConfig.detectMethod;

    // Declare variables once to avoid redeclaration errors
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth <= breakpoint;
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileKeywords = ['mobile', 'android', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
    const isMobileUserAgent = mobileKeywords.some(keyword => userAgent.includes(keyword));
    const prefersTouchInterface = window.matchMedia('(pointer: coarse)').matches;

    switch (method) {
        case 'screensize':
            return isSmallScreen;

        case 'touch':
            return hasTouchScreen || prefersTouchInterface;

        case 'useragent':
            return isMobileUserAgent;

        case 'auto':
        default:
            // Combined approach: touch + small screen, or touch interface, or user agent
            return (hasTouchScreen && isSmallScreen) || prefersTouchInterface || isMobileUserAgent;
    }
}

// Function to check if element is in viewport
function isInViewport(element) {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    // Element is in viewport if its top is above 90% of viewport height
    return rect.top <= windowHeight * 0.9;
}

// Function to apply animations based on data attributes
function applyAnimations() {
    const elements = document.querySelectorAll('[data-scroll-animation]');
    console.log(`Found ${elements.length} elements with scroll animations`);

    elements.forEach(el => {
        const animationType = el.getAttribute('data-scroll-animation');
        const start = el.getAttribute('data-scroll-start') || defaultConfig.start;
        const durationMs = parseInt(el.getAttribute('data-scroll-duration')) || defaultConfig.duration; // ms
        const delayMs = parseInt(el.getAttribute('data-scroll-delay')) || defaultConfig.delay; // ms
        const disableOnMobile = el.getAttribute('data-disable-mobile') === 'true' || defaultConfig.disableOnMobile;
        const playOnce = el.getAttribute('data-play-once') === 'true' || defaultConfig.playOnce;
        const animateOnLoadOnly = el.getAttribute('data-animate-on-load-only') === 'true' || defaultConfig.animateOnLoadOnly;

        // Skip animation on mobile if disabled
        if (disableOnMobile && isMobile()) {
            console.log(`Skipping animation on mobile for: ${animationType}`);
            // Set element to final state immediately
            const animationConfig = animationLibrary[animationType];
            if (animationConfig) {
                gsap.set(el, animationConfig.to);
            }
            return;
        }

        // Convert both duration and delay to seconds
        const [duration, delay] = msToSeconds(durationMs, delayMs);

        const animationConfig = animationLibrary[animationType];

        if (animationConfig) {
            // If animateOnLoadOnly is true, skip scroll trigger and animate immediately
            if (animateOnLoadOnly) {
                gsap.fromTo(el,
                    animationConfig.from,
                    {
                        ...animationConfig.to,
                        duration: duration,
                        delay: delay,
                        ease: "power2.out"
                    }
                );
                return;
            }

            // Check if element is already in viewport on page load
            const inViewportOnLoad = isInViewport(el);

            if (inViewportOnLoad) {
                // Animate immediately without ScrollTrigger
                gsap.fromTo(el,
                    animationConfig.from,
                    {
                        ...animationConfig.to,
                        duration: duration,
                        delay: delay,
                        ease: "power2.out"
                    }
                );
            } else {
                // Set initial state
                gsap.set(el, animationConfig.from);

                // Determine toggle actions based on playOnce setting
                const toggleActions = playOnce ? "play none none none" : "play none none reverse";

                // Create the animation with ScrollTrigger
                gsap.to(el, {
                    ...animationConfig.to,
                    duration: duration,
                    delay: delay,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: el,
                        start: start,
                        end: defaultConfig.end,
                        toggleActions: toggleActions,
                        onEnter: () => console.log(`Animation triggered for: ${animationType}`),
                        // markers: true
                    }
                });
            }
        } else {
            console.warn(`Animation type "${animationType}" not found in animation library`);
        }
    });
}
applyAnimations();
